
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Star, 
  Eye, 
  UserPlus,
  Calendar,
  FileText,
  ExternalLink
} from "lucide-react";
import type { SuggestedCandidate } from "@/types/candidateSuggestion";
import type { Job } from "../../types";

interface MatchingCandidatesListProps {
  candidates: SuggestedCandidate[];
  job: Job;
}

export const MatchingCandidatesList: React.FC<MatchingCandidatesListProps> = ({ 
  candidates, 
  job 
}) => {
  const getMatchScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-50';
    if (score >= 75) return 'text-blue-600 bg-blue-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'hired': return 'bg-green-100 text-green-800';
      case 'interviewing': return 'bg-blue-100 text-blue-800';
      case 'screening': return 'bg-purple-100 text-purple-800';
      case 'new': return 'bg-gray-100 text-gray-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
      {candidates.map((candidate) => (
        <Card key={candidate.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={candidate.resume_url || undefined} />
                  <AvatarFallback>
                    {candidate.first_name?.[0]}{candidate.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-lg">
                      {candidate.first_name} {candidate.last_name}
                    </h3>
                    <Badge className={getStatusColor(candidate.status || 'new')}>
                      {candidate.status || 'new'}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                    {candidate.experience_years && (
                      <span>{candidate.experience_years} years experience</span>
                    )}
                    {candidate.location_city && candidate.location_state && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{candidate.location_city}, {candidate.location_state}</span>
                      </div>
                    )}
                    {candidate.remote_ok && (
                      <Badge variant="outline" className="text-xs">Remote OK</Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {candidate.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        <span>{candidate.email}</span>
                      </div>
                    )}
                    {candidate.phone && (
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        <span>{candidate.phone}</span>
                      </div>
                    )}
                    {candidate.last_contact && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>Last contact: {new Date(candidate.last_contact).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getMatchScoreColor(candidate.match_score)}`}>
                  {candidate.match_score}% match
                </div>
                <Progress value={candidate.match_score} className="w-20 h-2" />
              </div>
            </div>

            {/* Skills */}
            {candidate.skills && candidate.skills.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium mb-2">Skills:</p>
                <div className="flex flex-wrap gap-1">
                  {candidate.skills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Match Reasons */}
            {candidate.match_reasons && candidate.match_reasons.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">Why this candidate matches:</p>
                <ul className="text-sm text-blue-700 space-y-1">
                  {candidate.match_reasons.map((reason, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Star className="h-3 w-3 fill-current" />
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Notes */}
            {candidate.notes && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium mb-1">Notes:</p>
                <p className="text-sm text-muted-foreground">{candidate.notes}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <Button size="sm">
                  <UserPlus className="h-4 w-4 mr-1" />
                  Add to Job
                </Button>
                <Button variant="outline" size="sm">
                  <Calendar className="h-4 w-4 mr-1" />
                  Schedule Interview
                </Button>
                <Button variant="outline" size="sm">
                  <Mail className="h-4 w-4 mr-1" />
                  Send Message
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm">
                  <Eye className="h-4 w-4 mr-1" />
                  View Profile
                </Button>
                {candidate.resume_url && (
                  <Button variant="ghost" size="sm">
                    <FileText className="h-4 w-4 mr-1" />
                    Resume
                  </Button>
                )}
                {candidate.linkedin_url && (
                  <Button variant="ghost" size="sm">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    LinkedIn
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
